"use strict";
/**
 * Same-Day availability: whether Same-Day can still be booked, and which
 * arrival windows are still on offer.
 *
 * See `docs/adr/0004-same-day-availability-server-side-only.md`. Every input
 * that describes "now" is a *parameter*. This module does not call `new Date()`,
 * does not read a timezone, and has no date library — deliberately, because
 * `packages/pricing` runs in the customer's browser as well as on the server,
 * and a browser answering "is Same-Day still available" from its own clock
 * answers for the customer's timezone rather than the business's. A customer in
 * Los Angeles would be offered a 4pm New York window at 4pm Pacific, three
 * hours after it closed.
 *
 * The caller that knows the business's wall clock is `apps/api`, which already
 * has `moment-timezone` for exactly this (precedent: `TaskResolver.ts:673-687`).
 * It resolves `businessToday` and `nowMinutes` and hands them in; the client
 * receives the answer as a value and renders it.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.flexibleDiscountCentsFor = exports.resolveSameDayAvailability = exports.isSameDayServiceDate = void 0;
const config_1 = require("./config");
/**
 * Whether a service date is the business's today.
 *
 * A string comparison of two calendar dates, which is the whole trick: both
 * sides are already business-local calendar dates, so there is no instant to
 * convert and nothing for a timezone to shift.
 */
function isSameDayServiceDate(serviceDate, clock) {
    var _a;
    const service = String(serviceDate !== null && serviceDate !== void 0 ? serviceDate : "").trim().split("T")[0];
    const today = String((_a = clock === null || clock === void 0 ? void 0 : clock.businessToday) !== null && _a !== void 0 ? _a : "").trim().split("T")[0];
    if (!/^\d{4}-\d{2}-\d{2}$/.test(service) || !/^\d{4}-\d{2}-\d{2}$/.test(today)) {
        throw new RangeError(`Same-Day comparison needs two YYYY-MM-DD calendar dates, got service ` +
            `${JSON.stringify(serviceDate)} and business today ${JSON.stringify(clock === null || clock === void 0 ? void 0 : clock.businessToday)}`);
    }
    return service === today;
}
exports.isSameDayServiceDate = isSameDayServiceDate;
function assertClock(clock) {
    if (!Number.isInteger(clock === null || clock === void 0 ? void 0 : clock.nowMinutes) ||
        clock.nowMinutes < 0 ||
        clock.nowMinutes > 1440) {
        throw new RangeError(`nowMinutes must be a whole number of minutes since business-local ` +
            `midnight (0-1440), got ${String(clock === null || clock === void 0 ? void 0 : clock.nowMinutes)}. ` +
            `packages/pricing cannot default it — it does not know what time it is ` +
            `where the business is.`);
    }
    return clock;
}
/**
 * Resolve Same-Day availability for a service date at a business-local instant.
 *
 * Both cutoffs apply, and both are needed: a per-window cutoff is what makes the
 * offered windows narrow through the day, and the per-day cutoff is what stops
 * Same-Day entirely even when a late window would technically still be open.
 *
 * A daily cutoff of `0` means **no daily cutoff**, not "closes at midnight".
 * Read literally, midnight has already passed at every instant of every day, so
 * a zero closed Same-Day permanently — and `CreatePricingConfigHistory` seeds
 * every day at `0`, which made that the shipped state of all seven. An admin who
 * switched a day on saw nothing change and no explanation why, because the toggle
 * they used and the setting vetoing it are separate controls.
 *
 * "Closed all day" is what the `enabledByDay` toggle already expresses, so
 * reading `0` that way gave one meaning two controls and left the more
 * discoverable one inert. Zero now means the day is gated only by its windows'
 * own cutoffs, which is both the useful default and the one an operator assumes
 * from an untouched field.
 */
function resolveSameDayAvailability(serviceDate, clock, config) {
    assertClock(clock);
    const dayOfWeek = (0, config_1.dayOfWeekForServiceDate)(serviceDate);
    const isSameDay = isSameDayServiceDate(serviceDate, clock);
    const unavailable = (reason) => ({
        isSameDay,
        available: false,
        reason,
        windows: [],
        dayOfWeek,
    });
    if (!isSameDay)
        return unavailable("not_same_day");
    if (!config.sameDay.enabledByDay[dayOfWeek])
        return unavailable("disabled_for_day");
    const dailyCutoff = config.sameDay.cutoffMinutesByDay[dayOfWeek];
    if (dailyCutoff > 0 && clock.nowMinutes >= dailyCutoff) {
        return unavailable("past_daily_cutoff");
    }
    /**
     * A window closes at its own cutoff *or* when it opens, whichever is sooner.
     *
     * The clamp is what stops a window being offered after it has already
     * happened. `cutoffMinutes` is meant to be the window's start less the notice
     * the team needs, but nothing validates that: `assertValidPricingConfig`
     * checks a window ends after it starts and never compares the cutoff to
     * either. One row with a cutoff past its start — a bad admin write, an
     * imported config, a hand-edited history row — and `12:00-3:00pm` stays on
     * offer at five past three, which is the same lie as the fixed-time grid
     * offering 8:00am in the afternoon.
     *
     * Clamped here rather than rejected in validation on purpose. Validation runs
     * on *read* as well as write, so tightening it would make an existing bad row
     * throw inside `loadCurrentPricingConfig` and take the whole pricing endpoint
     * down with it — turning a wrong window into a dead funnel. Clamping fixes the
     * data that is already stored, and costs a correctly configured window
     * nothing: its cutoff is at or before its start, so the minimum is the cutoff.
     */
    const windows = config.sameDay.windows.filter((window) => 
    /**
     * Switched off, or not scheduled on this weekday, and it is not on offer —
     * checked before the clock, because a window that does not run today has
     * no cutoff worth comparing. `windowOffersDay` owns the defaults, so a
     * window stored before these fields existed still answers true here.
     */
    (0, config_1.windowOffersDay)(window, dayOfWeek) &&
        clock.nowMinutes < Math.min(window.cutoffMinutes, window.startMinutes));
    if (windows.length === 0)
        return unavailable("all_windows_closed");
    return { isSameDay, available: true, windows, dayOfWeek };
}
exports.resolveSameDayAvailability = resolveSameDayAvailability;
/**
 * The Flexible Time discount for a service date, in cents.
 *
 * Flexible Time and Same-Day are mutually exclusive (see `CONTEXT.md`), which
 * the engine enforces; this function only reads the per-day amount.
 */
function flexibleDiscountCentsFor(serviceDate, config) {
    return config.flexibleDiscountCents[(0, config_1.dayOfWeekForServiceDate)(serviceDate)];
}
exports.flexibleDiscountCentsFor = flexibleDiscountCentsFor;
//# sourceMappingURL=same-day.js.map